/**
 * AuthViewModel - Cross-Platform Authentication Flow ViewModel
 * 
 * Handles all 7 authentication flows with comprehensive state management:
 * - Phone sign-in flow → SMS verification
 * - Email sign-in flow → Email code verification  
 * - Password sign-in flow
 * - Password reset flow
 * - Sign-up flow with complete account information
 * 
 * Features:
 * - Multi-step flow navigation
 * - Real-time validation integration
 * - Operation-specific loading and error states
 * - Countdown timers for code resend functionality
 * - Cross-platform service integration via dependency injection
 * - SessionViewModel integration for login/logout coordination
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import type {
  AuthResponse,
  AuthToken,
  SignInCredentials,
  SignUpData,
  PasswordResetRequest,
  ClientUser,
  ProfileUpdate
} from '@restaurant/core'
import { getAuthErrorMessage, validateFullName } from '@restaurant/core'
import type { IPlatformServices } from '../contracts'
import { safeGetLogger } from '@restaurant/logging'

// Auth flow types
export type AuthFlow =
  | 'signin-phone'
  | 'signin-email'
  | 'enter-sms-code'
  | 'enter-email-code'
  | 'signin-password'
  | 'reset-password'
  | 'set-new-password'
  | 'signup'

// Account information interface for sign-up
export interface AccountInformation {
  fullName?: string
  emailAddress?: string
  phoneNumber?: string
  birthday?: string
}

// Auth service interface
export interface AuthService {
  signIn(credentials: SignInCredentials): Promise<AuthResponse>
  signUp(userData: SignUpData): Promise<AuthResponse>
  signInWithPhone(phoneNumber: string): Promise<void>
  verifyPhoneOTP(phoneNumber: string, otp: string): Promise<AuthResponse>
  resetPassword(request: PasswordResetRequest): Promise<void>
  setNewPassword(newPassword: string): Promise<void>
  verifyEmail(token: string): Promise<{ verified: boolean; message: string }>
  getCurrentUser(): Promise<ClientUser>
  updateProfile(updates: ProfileUpdate): Promise<ClientUser>
  // Additional methods for email magic link flow
  signInWithEmail?(email: string): Promise<void>
  verifyEmailOTP?(email: string, otp: string): Promise<AuthResponse>
}

// Session ViewModel interface  
export interface SessionViewModel {
  login(user: ClientUser, token: AuthToken): void
  logout(): void
  user: ClientUser | null
  isAuthenticated: boolean
}

// Validation ViewModel interface
export interface ValidationViewModel {
  validatePhone(phone: string): { isValid: boolean; error: string | null }
  validateEmail(email: string): { isValid: boolean; error: string | null }
  validateForm(data: AccountInformation | Record<string, unknown>): { isValid: boolean; errors: Record<string, string> }
  clearErrors(): void
  errors: Record<string, string>
}

// AuthViewModel state interface
export interface AuthViewModelState {
  // Current flow state
  currentFlow: AuthFlow
  
  // Form input states
  phoneNumber: string
  email: string
  password: string
  newPassword: string
  confirmPassword: string
  smsCode: string
  emailCode: string
  accountInformation: AccountInformation
  
  // UI states
  loading: boolean
  errors: Record<string, string>
  
  // Validation states
  isPhoneValid: boolean
  isEmailValid: boolean
  isPasswordValid: boolean
  canContinue: boolean
  
  // Countdown timers
  smsResendCountdown: number
  emailResendCountdown: number
  /** Post-submit cooldown for the password-reset send, throttling repeat recovery-email sends. */
  resetPasswordCountdown: number
  canResendSms: boolean
  canResendEmail: boolean
}

// AuthViewModel actions interface
export interface AuthViewModelActions {
  // Input setters
  setPhoneNumber(phone: string): void
  setEmail(email: string): void
  setPassword(password: string): void
  setNewPassword(password: string): void
  setConfirmPassword(password: string): void
  setSmsCode(code: string): void
  setEmailCode(code: string): void
  setAccountInformation(info: AccountInformation): void

  // Auth flow actions
  signInWithPhone(): Promise<void>
  signInWithEmail(): Promise<void>
  verifySmsCode(): Promise<void>
  verifyEmailCode(): Promise<void>
  signInWithPassword(): Promise<void>
  sendPasswordResetInstructions(): Promise<void>
  setNewPasswordWithRecoverySession(): Promise<void>
  signUp(): Promise<void>
  completeProfile(): Promise<void>
  
  // Resend actions
  resendSmsCode(): Promise<void>
  resendEmailCode(): Promise<void>
  
  // Flow navigation
  switchToEmailSignIn(): void
  switchToPhoneSignIn(): void
  switchToPasswordSignIn(): void
  switchToResetPassword(): void
  switchToSignInWithLink(): void
  switchToSignInInstead(): void
  cancelSmsFlow(): void
  cancelEmailFlow(): void
  cancelPasswordReset(): void
  setCurrentFlow(flow: AuthFlow): void
  
  // Utility actions
  clearErrors(): void
}

// Combined AuthViewModel interface
export interface AuthViewModel extends AuthViewModelState, AuthViewModelActions {}

/**
 * Create platform-agnostic AuthViewModel factory
 * Uses dependency injection for platform-specific services
 */
export const createAuthViewModel = (
  platformServices: IPlatformServices,
  authService: AuthService,
  sessionViewModel: SessionViewModel,
  validationViewModel: ValidationViewModel
) => {
  return (): AuthViewModel => {
    // Core state
    const [currentFlow, setCurrentFlowState] = useState<AuthFlow>('signin-phone')
    const [phoneNumber, setPhoneNumberState] = useState('')
    const [email, setEmailState] = useState('')
    const [password, setPasswordState] = useState('')
    const [newPassword, setNewPasswordState] = useState('')
    const [confirmPassword, setConfirmPasswordState] = useState('')
    const [smsCode, setSmsCodeState] = useState('')
    const [emailCode, setEmailCodeState] = useState('')
    const [accountInformation, setAccountInformationState] = useState<AccountInformation>({})
    const [loading, setLoading] = useState(false)
    const [errors, setErrors] = useState<Record<string, string>>({})
    
    // Countdown timers
    const [smsResendCountdown, setSmsResendCountdown] = useState(0)
    const [emailResendCountdown, setEmailResendCountdown] = useState(0)
    const [resetPasswordCountdown, setResetPasswordCountdown] = useState(0)

    // Synchronous in-flight lock for OTP verification. `loading` is async React
    // state, so rapid confirm taps / re-render re-fires all read the old value
    // and each call verifyPhoneOTP before setLoading(true) commits — the prod
    // 9x burst against an already-consumed code. A ref updates synchronously, so
    // re-entry bails before a second network call. Shared across SMS + email
    // verify; the user can only be on one OTP screen at a time.
    const verifyOtpInFlightRef = useRef(false)

    // Auth token stashed during OTP verification for reuse in completeProfile
    const [storedAuthToken, setStoredAuthToken] = useState<AuthToken>({
      accessToken: '',
      refreshToken: '',
      expiresIn: 0,
      tokenType: 'Bearer',
    })
    
    // Timer effects for countdown
    useEffect(() => {
      if (smsResendCountdown > 0) {
        const timer = setTimeout(() => {
          setSmsResendCountdown(prev => prev - 1)
        }, 1000)
        return () => clearTimeout(timer)
      }
      return undefined
    }, [smsResendCountdown])
    
    useEffect(() => {
      if (emailResendCountdown > 0) {
        const timer = setTimeout(() => {
          setEmailResendCountdown(prev => prev - 1)
        }, 1000)
        return () => clearTimeout(timer)
      }
      return undefined
    }, [emailResendCountdown])

    useEffect(() => {
      if (resetPasswordCountdown > 0) {
        const timer = setTimeout(() => {
          setResetPasswordCountdown(prev => prev - 1)
        }, 1000)
        return () => clearTimeout(timer)
      }
      return undefined
    }, [resetPasswordCountdown])

    // Validation states
    const phoneValidation = useMemo(() => 
      validationViewModel.validatePhone(phoneNumber), [phoneNumber, validationViewModel])
    const emailValidation = useMemo(() => 
      validationViewModel.validateEmail(email), [email, validationViewModel])
    
    const isPhoneValid = phoneValidation.isValid
    const isEmailValid = emailValidation.isValid
    const isPasswordValid = password.length >= 8 // Basic password validation
    
    // Compute canContinue based on current flow
    const canContinue = useMemo(() => {
      switch (currentFlow) {
        case 'signin-phone':
          return isPhoneValid && !loading
        case 'signin-email':
          return isEmailValid && !loading
        case 'signin-password':
          return isEmailValid && isPasswordValid && !loading
        case 'enter-sms-code':
          return smsCode.length === 6 && !loading
        case 'enter-email-code':
          return emailCode.length === 6 && !loading
        case 'reset-password':
          return isEmailValid && !loading && resetPasswordCountdown === 0
        case 'set-new-password': {
          const valid =
            newPassword.length >= 8 &&
            confirmPassword.length >= 8 &&
            newPassword === confirmPassword
          return valid && !loading
        }
        case 'signup': {
          const accountValid = validationViewModel.validateForm(accountInformation)
          return accountValid.isValid && !loading
        }
        default:
          return false
      }
    }, [currentFlow, isPhoneValid, isEmailValid, isPasswordValid, smsCode, emailCode, accountInformation, loading, validationViewModel, newPassword, confirmPassword, resetPasswordCountdown])
    
    // Computed countdown states
    const canResendSms = smsResendCountdown === 0
    const canResendEmail = emailResendCountdown === 0
    
    // Helper function for error handling. Callers may pass an already
    // user-friendly `message` (e.g. mapped via getAuthErrorMessage); otherwise
    // the raw error message is shown.
    const handleError = useCallback((error: Error, field: string, message?: string) => {
      setErrors(prev => ({ ...prev, [field]: message ?? error.message }))
      setLoading(false)
    }, [])
    
    // Helper function for successful auth completion
    const completeAuth = useCallback(async (authResponse: AuthResponse) => {
      sessionViewModel.login(authResponse.user, authResponse.token)
      setLoading(false)
      setErrors({})
      
      // Navigate to home using platform service
      platformServices.navigationService.navigateToHome()
    }, [sessionViewModel, platformServices.navigationService])
    
    // Input setters with validation
    const setPhoneNumber = useCallback((phone: string) => {
      setPhoneNumberState(phone)
      const validation = validationViewModel.validatePhone(phone)
      if (!validation.isValid && validation.error) {
        setErrors(prev => ({ ...prev, phone: validation.error! }))
      } else {
        setErrors(prev => ({ ...prev, phone: '' }))
      }
    }, [validationViewModel])
    
    const setEmail = useCallback((emailValue: string) => {
      setEmailState(emailValue)
      const validation = validationViewModel.validateEmail(emailValue)
      if (!validation.isValid && validation.error) {
        setErrors(prev => ({ ...prev, email: validation.error! }))
      } else {
        setErrors(prev => ({ ...prev, email: '' }))
      }
    }, [validationViewModel])
    
    const setPassword = useCallback((passwordValue: string) => {
      setPasswordState(passwordValue)
      if (passwordValue.length > 0 && passwordValue.length < 8) {
        setErrors(prev => ({ ...prev, password: 'Password must be at least 8 characters' }))
      } else {
        setErrors(prev => ({ ...prev, password: '' }))
      }
    }, [])

    const setNewPassword = useCallback((value: string) => {
      setNewPasswordState(value)
      if (value.length > 0 && value.length < 8) {
        setErrors(prev => ({ ...prev, setNewPassword: 'Password must be at least 8 characters' }))
      } else {
        setErrors(prev => ({ ...prev, setNewPassword: '' }))
      }
    }, [])

    const setConfirmPassword = useCallback((value: string) => {
      setConfirmPasswordState(value)
      // Clear any prior mismatch warning eagerly; final mismatch is reported on submit.
      setErrors(prev => ({ ...prev, setNewPassword: '' }))
    }, [])

    const setSmsCode = useCallback((code: string) => {
      // Only allow numeric input, max 6 digits
      const numericCode = code.replace(/\D/g, '').slice(0, 6)
      setSmsCodeState(numericCode)
      if (errors.smsCode) {
        setErrors(prev => ({ ...prev, smsCode: '' }))
      }
    }, [errors.smsCode])
    
    const setEmailCode = useCallback((code: string) => {
      // Only allow numeric input, max 6 digits
      const numericCode = code.replace(/\D/g, '').slice(0, 6)
      setEmailCodeState(numericCode)
      if (errors.emailCode) {
        setErrors(prev => ({ ...prev, emailCode: '' }))
      }
    }, [errors.emailCode])
    
    const setAccountInformation = useCallback((info: AccountInformation) => {
      setAccountInformationState(info)
      // Clear any previous form validation errors
      const validation = validationViewModel.validateForm(info)
      if (validation.isValid) {
        setErrors(prev => {
          const newErrors = { ...prev }
          Object.keys(validation.errors || {}).forEach(key => {
            delete newErrors[key]
          })
          return newErrors
        })
      }
    }, [validationViewModel])
    
    // Phone sign-in flow
    const signInWithPhone = useCallback(async () => {
      if (!isPhoneValid) {
        setErrors(prev => ({ ...prev, phone: phoneValidation.error || 'Invalid phone number' }))
        return
      }
      
      setLoading(true)
      setErrors(prev => ({ ...prev, phone: '' }))
      
      try {
        await authService.signInWithPhone(phoneNumber)
        safeGetLogger()?.auth('otp_requested', { phoneNumber })
        setCurrentFlowState('enter-sms-code')
        setSmsResendCountdown(30)
        setLoading(false)
      } catch (error) {
        const err = error as Error
        safeGetLogger()?.auth('otp_request_failed', {
          phoneNumber,
          errorMessage: err.message,
          errorName: err.name,
          stack: err.stack,
        })
        handleError(err, 'phone', getAuthErrorMessage(err))
      }
    }, [phoneNumber, isPhoneValid, phoneValidation.error, authService, handleError])
    
    // Email sign-in flow
    const signInWithEmail = useCallback(async () => {
      if (!isEmailValid) {
        setErrors(prev => ({ ...prev, email: emailValidation.error || 'Invalid email address' }))
        return
      }
      
      setLoading(true)
      setErrors(prev => ({ ...prev, email: '' }))
      
      try {
        // Use email sign-in service if available, otherwise fallback to phone flow pattern
        if (authService.signInWithEmail) {
          await authService.signInWithEmail(email)
          setCurrentFlowState('enter-email-code')
          setEmailResendCountdown(30)
        } else {
          // Fallback - treat as email/password flow
          setCurrentFlowState('signin-password')
        }
        setLoading(false)
      } catch (error) {
        handleError(error as Error, 'email')
      }
    }, [email, isEmailValid, emailValidation.error, authService, handleError])
    
    // SMS code verification
    const verifySmsCode = useCallback(async () => {
      if (smsCode.length !== 6) {
        setErrors(prev => ({ ...prev, smsCode: 'Please enter a 6-digit code' }))
        return
      }

      // Drop re-entry while a verify is in flight — see verifyOtpInFlightRef.
      if (verifyOtpInFlightRef.current) return
      verifyOtpInFlightRef.current = true

      setLoading(true)
      setErrors(prev => ({ ...prev, smsCode: '' }))

      try {
        const authResponse = await authService.verifyPhoneOTP(phoneNumber, smsCode)
        safeGetLogger()?.auth('otp_verified', { phoneNumber })

        // Check profile completeness — if either name field is missing, collect profile info
        // before navigating home. User is authenticated; just profile data is incomplete.
        const { firstName, lastName } = authResponse.user
        if (!firstName || !lastName) {
          // Don't call sessionViewModel.login() here — Zustand's sync update
          // triggers DesktopLayout re-render before React batches the flow state
          // change, causing premature navigation to /account. The API client
          // already has the token from verifyPhoneOTP; completeProfile() will
          // call login() after the user fills in their info.
          setCurrentFlowState('signup')
          setStoredAuthToken(authResponse.token)
          // Strip country code for display ("+16264834818" → "6264834818")
          const rawPhone = (authResponse.user.phoneNumber || phoneNumber || '').replace(/\D/g, '')
          const displayPhone = rawPhone.length === 11 && rawPhone.startsWith('1') ? rawPhone.slice(1) : rawPhone

          setAccountInformationState({
            fullName: [authResponse.user.firstName, authResponse.user.lastName].filter(Boolean).join(' ') || undefined,
            emailAddress: authResponse.user.email || undefined,
            phoneNumber: displayPhone || undefined,
          })
          setLoading(false)
          setErrors({})
        } else {
          await completeAuth(authResponse)
        }
      } catch (error) {
        const err = error as Error
        safeGetLogger()?.auth('otp_verification_failed', {
          phoneNumber,
          errorMessage: err.message,
          errorName: err.name,
          stack: err.stack,
        })
        // Map the expected expired/already-consumed-OTP error to a clear
        // "request a new one" prompt instead of the raw Supabase string.
        handleError(err, 'smsCode', getAuthErrorMessage(err))
      } finally {
        verifyOtpInFlightRef.current = false
      }
    }, [smsCode, phoneNumber, authService, completeAuth, handleError, sessionViewModel])
    
    // Email code verification
    const verifyEmailCode = useCallback(async () => {
      if (emailCode.length !== 6) {
        setErrors(prev => ({ ...prev, emailCode: 'Please enter a 6-digit code' }))
        return
      }

      // Drop re-entry while a verify is in flight — see verifyOtpInFlightRef.
      if (verifyOtpInFlightRef.current) return
      verifyOtpInFlightRef.current = true

      setLoading(true)
      setErrors(prev => ({ ...prev, emailCode: '' }))

      try {
        if (authService.verifyEmailOTP) {
          const authResponse = await authService.verifyEmailOTP(email, emailCode)

          // Check profile completeness — if either name field is missing, collect profile info
          // before navigating home. User is authenticated; just profile data is incomplete.
          const { firstName, lastName } = authResponse.user
          if (!firstName || !lastName) {
            // Don't call sessionViewModel.login() here — same Zustand race as phone flow.
            // completeProfile() will call login() after profile collection.
            setCurrentFlowState('signup')
            setStoredAuthToken(authResponse.token)
            // Strip country code for display
            const rawPhone = (authResponse.user.phoneNumber || '').replace(/\D/g, '')
            const displayPhone = rawPhone.length === 11 && rawPhone.startsWith('1') ? rawPhone.slice(1) : rawPhone

            setAccountInformationState({
              fullName: [authResponse.user.firstName, authResponse.user.lastName].filter(Boolean).join(' ') || undefined,
              emailAddress: authResponse.user.email || email,
              phoneNumber: displayPhone || undefined,
            })
            setLoading(false)
            setErrors({})
          } else {
            await completeAuth(authResponse)
          }
        } else {
          throw new Error('Email verification not supported')
        }
      } catch (error) {
        // Same expired-OTP mapping as the SMS path.
        handleError(error as Error, 'emailCode', getAuthErrorMessage(error))
      } finally {
        verifyOtpInFlightRef.current = false
      }
    }, [emailCode, email, authService, completeAuth, handleError, sessionViewModel])
    
    // Password sign-in
    const signInWithPassword = useCallback(async () => {
      if (!isEmailValid || !isPasswordValid) {
        if (!isEmailValid) {
          setErrors(prev => ({ ...prev, email: emailValidation.error || 'Invalid email address' }))
        }
        if (!isPasswordValid) {
          setErrors(prev => ({ ...prev, password: 'Password must be at least 8 characters' }))
        }
        return
      }
      
      setLoading(true)
      setErrors(prev => ({ ...prev, signin: '' }))
      
      try {
        const credentials: SignInCredentials = { email, password }
        const authResponse = await authService.signIn(credentials)
        await completeAuth(authResponse)
      } catch (error) {
        // Map transport/edge errors (e.g. a Supabase 504) and bad credentials to
        // a clear, retry-able message rather than an opaque/stringified error.
        handleError(error as Error, 'signin', getAuthErrorMessage(error))
      }
    }, [email, password, isEmailValid, isPasswordValid, emailValidation.error, authService, completeAuth, handleError])
    
    // Password reset
    const sendPasswordResetInstructions = useCallback(async () => {
      if (!isEmailValid) {
        setErrors(prev => ({ ...prev, email: emailValidation.error || 'Invalid email address' }))
        return
      }

      setLoading(true)
      setErrors(prev => ({ ...prev, resetPassword: '' }))
      // Start the cooldown on every valid attempt (success OR failure) so rapid
      // re-taps can't fire repeated recovery-email sends and trip the GoTrue
      // rate limit faster. Matches the Supabase Auth "Minimum interval per user"
      // (60s) — a shorter client cooldown leaves a window where re-taps are
      // guaranteed to fail server-side with "Error sending recovery email".
      setResetPasswordCountdown(60)

      try {
        const request: PasswordResetRequest = { email }
        await authService.resetPassword(request)
        setLoading(false)
        // Could navigate to success screen or show success message
      } catch (error) {
        handleError(error as Error, 'resetPassword')
      }
    }, [email, isEmailValid, emailValidation.error, authService, handleError])

    // Commit the new password using the recovery session Supabase established
    // from the email-link hash. No currentPassword required (recovery flow).
    const setNewPasswordWithRecoverySession = useCallback(async () => {
      if (newPassword.length < 8) {
        setErrors(prev => ({ ...prev, setNewPassword: 'Password must be at least 8 characters' }))
        return
      }
      if (newPassword !== confirmPassword) {
        setErrors(prev => ({ ...prev, setNewPassword: 'Passwords do not match' }))
        return
      }

      setLoading(true)
      setErrors(prev => ({ ...prev, setNewPassword: '' }))

      try {
        await authService.setNewPassword(newPassword)
        setLoading(false)
        // Clear inputs and close the sheet by returning to a known flow state.
        setNewPasswordState('')
        setConfirmPasswordState('')
        // Supabase recovery session leaves the user authenticated. Reset the
        // flow to phone sign-in so the next open of the sheet starts fresh.
        setCurrentFlowState('signin-phone')
      } catch (error) {
        handleError(error as Error, 'setNewPassword')
      }
    }, [newPassword, confirmPassword, authService, handleError])
    
    // Legacy signUp flow — not used for OTP-based profile completion.
    // Post-OTP users go through completeProfile() instead.
    // TODO: supply real password handling before re-enabling for email/password registration.
    const signUp = useCallback(async () => {
      const validation = validationViewModel.validateForm(accountInformation)
      if (!validation.isValid) {
        setErrors(prev => ({ ...prev, ...validation.errors }))
        return
      }

      // Require a first AND last name. The backend rejects an empty last_name
      // (min_length=1 → 422) and Clover rejects a blank last name at customer
      // creation, so a single-word name must not be allowed to land.
      const nameError = validateFullName(accountInformation.fullName)
      if (nameError) {
        platformServices.platformService.showToast(nameError)
        setErrors(prev => ({ ...prev, signup: nameError }))
        return
      }

      setLoading(true)
      setErrors(prev => ({ ...prev, signup: '' }))
      
      try {
        // Parse full name into first/last name
        const nameParts = (accountInformation.fullName || '').trim().split(' ')
        const firstName = nameParts[0] || ''
        const lastName = nameParts.slice(1).join(' ') || ''
        
        const signUpData: SignUpData = {
          email: accountInformation.emailAddress || '',
          password: '', // Password would be handled separately or generated
          firstName,
          lastName,
          phoneNumber: accountInformation.phoneNumber,
        }
        
        const authResponse = await authService.signUp(signUpData)
        await completeAuth(authResponse)
      } catch (error) {
        handleError(error as Error, 'signup')
      }
    }, [accountInformation, validationViewModel, authService, completeAuth, handleError, platformServices.platformService])
    
    // Complete profile for post-OTP users (already authenticated, profile data missing)
    const completeProfile = useCallback(async () => {
      const validation = validationViewModel.validateForm(accountInformation)
      if (!validation.isValid) {
        setErrors(prev => ({ ...prev, ...validation.errors }))
        return
      }

      // Require a first AND last name — this is the origin of blank last names
      // (single-word OTP names split to lastName=''). The backend rejects an
      // empty last_name (422) and Clover rejects it at card-add time.
      const nameError = validateFullName(accountInformation.fullName)
      if (nameError) {
        platformServices.platformService.showToast(nameError)
        setErrors(prev => ({ ...prev, signup: nameError }))
        return
      }

      if (!storedAuthToken.accessToken) {
        handleError(new Error('No auth token — complete OTP verification first'), 'signup')
        return
      }

      setLoading(true)
      setErrors(prev => ({ ...prev, signup: '' }))

      try {
        // Parse full name into first/last name
        const nameParts = (accountInformation.fullName || '').trim().split(' ')
        const firstName = nameParts[0] || ''
        const lastName = nameParts.slice(1).join(' ') || ''

        // Convert birthday from display format ("Jan 21") to YYYY-MM-DD for backend.
        // Pass through if already in YYYY-MM-DD format.
        let birthdayISO: string | undefined
        if (accountInformation.birthday) {
          if (/^\d{4}-\d{2}-\d{2}$/.test(accountInformation.birthday)) {
            birthdayISO = accountInformation.birthday
          } else {
            const monthsAbbrev: Record<string, string> = {
              Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06',
              Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12',
            }
            const monthsFull: Record<string, string> = {
              January: '01', February: '02', March: '03', April: '04', May: '05', June: '06',
              July: '07', August: '08', September: '09', October: '10', November: '11', December: '12',
            }
            const parts = accountInformation.birthday.split(' ')
            const monthPart = parts[0]
            const dayPart = parts[1]
            if (monthPart && dayPart) {
              const mm = monthsAbbrev[monthPart] || monthsFull[monthPart]
              if (mm) {
                const day = dayPart.padStart(2, '0')
                birthdayISO = `2000-${mm}-${day}`
              }
            }
          }
        }

        // Normalize phone to E.164 for backend (e.g. "626-483-4818" → "+16264834818")
        let phoneE164: string | undefined
        if (accountInformation.phoneNumber) {
          const digits = accountInformation.phoneNumber.replace(/\D/g, '')
          if (digits.length === 10) phoneE164 = `+1${digits}`
          else if (digits.length === 11 && digits.startsWith('1')) phoneE164 = `+${digits}`
          else phoneE164 = digits.startsWith('+') ? digits : `+${digits}`
        }

        // Send profile updates to backend — response is partial (UserMetadata),
        // so we don't use it to construct the session user.
        await authService.updateProfile({
          firstName,
          lastName,
          birthday: birthdayISO,
          email: accountInformation.emailAddress,
          phoneNumber: phoneE164,
        })

        // Construct the session user from the Supabase auth data + form inputs.
        // The API response only has the updated fields, not a full ClientUser.
        const sessionUser = {
          ...({} as any), // base for optional ClientUser fields
          id: storedAuthToken.accessToken ? '' : '', // will be overridden below
          firstName,
          lastName,
          email: accountInformation.emailAddress || '',
          phoneNumber: phoneE164 || accountInformation.phoneNumber || '',
          birthday: birthdayISO || '',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          loyaltyPoints: 0,
          emailVerified: false,
          phoneVerified: true,
        }

        // Try to extract user ID from the stored Supabase JWT
        try {
          const encodedPayload = storedAuthToken.accessToken.split('.')[1]
          if (!encodedPayload) throw new Error('Malformed JWT')
          const payload = JSON.parse(atob(encodedPayload))
          sessionUser.id = payload.sub || ''
        } catch {
          // If JWT parsing fails, leave id empty — non-critical for navigation
        }

        sessionViewModel.login(sessionUser, storedAuthToken)
        setLoading(false)
        setErrors({})
        platformServices.navigationService.navigateToHome()
      } catch (error) {
        handleError(error as Error, 'signup')
      }
    }, [accountInformation, validationViewModel, authService, sessionViewModel, storedAuthToken, platformServices.navigationService, platformServices.platformService, handleError])

    // Resend actions
    const resendSmsCode = useCallback(async () => {
      if (!canResendSms) return
      
      setLoading(true)
      try {
        await authService.signInWithPhone(phoneNumber)
        setSmsResendCountdown(30)
        setLoading(false)
      } catch (error) {
        handleError(error as Error, 'smsCode')
      }
    }, [canResendSms, phoneNumber, authService, handleError])
    
    const resendEmailCode = useCallback(async () => {
      if (!canResendEmail || !authService.signInWithEmail) return
      
      setLoading(true)
      try {
        await authService.signInWithEmail(email)
        setEmailResendCountdown(30)
        setLoading(false)
      } catch (error) {
        handleError(error as Error, 'emailCode')
      }
    }, [canResendEmail, email, authService, handleError])
    
    // Flow navigation
    const setCurrentFlow = useCallback((flow: AuthFlow) => {
      setCurrentFlowState(flow)
      validationViewModel.clearErrors()
      setErrors({})
    }, [validationViewModel])
    
    const switchToEmailSignIn = useCallback(() => {
      setCurrentFlow('signin-email')
    }, [setCurrentFlow])

    const switchToPhoneSignIn = useCallback(() => {
      setCurrentFlow('signin-phone')
    }, [setCurrentFlow])

    const switchToPasswordSignIn = useCallback(() => {
      setCurrentFlow('signin-password')
    }, [setCurrentFlow])
    
    const switchToResetPassword = useCallback(() => {
      setCurrentFlow('reset-password')
    }, [setCurrentFlow])
    
    const switchToSignInWithLink = useCallback(() => {
      setCurrentFlow('signin-email')
    }, [setCurrentFlow])
    
    const switchToSignInInstead = useCallback(() => {
      setCurrentFlow('signin-phone')
      setAccountInformationState({})
      setStoredAuthToken({ accessToken: '', refreshToken: '', expiresIn: 0, tokenType: 'Bearer' })
    }, [setCurrentFlow])
    
    const cancelSmsFlow = useCallback(() => {
      setCurrentFlow('signin-phone')
      setSmsCodeState('')
      setSmsResendCountdown(0)
      setStoredAuthToken({ accessToken: '', refreshToken: '', expiresIn: 0, tokenType: 'Bearer' })
    }, [setCurrentFlow])
    
    const cancelEmailFlow = useCallback(() => {
      setCurrentFlow('signin-email')
      setEmailCodeState('')
      setEmailResendCountdown(0)
      setStoredAuthToken({ accessToken: '', refreshToken: '', expiresIn: 0, tokenType: 'Bearer' })
    }, [setCurrentFlow])
    
    const cancelPasswordReset = useCallback(() => {
      setCurrentFlow('signin-password')
    }, [setCurrentFlow])
    
    const clearErrors = useCallback(() => {
      setErrors({})
      validationViewModel.clearErrors()
    }, [validationViewModel])
    
    return {
      // State
      currentFlow,
      phoneNumber,
      email,
      password,
      newPassword,
      confirmPassword,
      smsCode,
      emailCode,
      accountInformation,
      loading,
      errors,
      isPhoneValid,
      isEmailValid,
      isPasswordValid,
      canContinue,
      smsResendCountdown,
      emailResendCountdown,
      resetPasswordCountdown,
      canResendSms,
      canResendEmail,

      // Actions
      setPhoneNumber,
      setEmail,
      setPassword,
      setNewPassword,
      setConfirmPassword,
      setSmsCode,
      setEmailCode,
      setAccountInformation,
      signInWithPhone,
      signInWithEmail,
      verifySmsCode,
      verifyEmailCode,
      signInWithPassword,
      sendPasswordResetInstructions,
      setNewPasswordWithRecoverySession,
      signUp,
      completeProfile,
      resendSmsCode,
      resendEmailCode,
      switchToEmailSignIn,
      switchToPhoneSignIn,
      switchToPasswordSignIn,
      switchToResetPassword,
      switchToSignInWithLink,
      switchToSignInInstead,
      cancelSmsFlow,
      cancelEmailFlow,
      cancelPasswordReset,
      setCurrentFlow,
      clearErrors,
    }
  }
}